import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import classes from "./MonthViewContainer.module.scss";
import useFilteredUserGroups from "../hooks/useFilteredUserGroups";
import {
  currentMonthDataState,
  updateShiftTransmittedStatusMonth,
} from "../../features/monthViewSlice";
import MonthViewHeader from "./MonthViewHeader";
import MonthView from "./MonthView";
import { fetchResourcePermissions } from "../../features/resourcePepermissionsSlice";
import { checkAccess, getToken } from "../services/userRegisterService";
import { alphabetSort } from "../../utils/utils";
import useFetchAirCrafts from "../hooks/useFetchAirCrafts";
import {
  API_BASE_URL,
  HEADER_SUBSCRIPTION_KEY,
} from "../services/global";
import { selectedLocationState } from "../../features/currentDataSlice";

const MonthViewContainer = () => {
  const currentMonthData = useSelector(currentMonthDataState);
  const [updatedMonth, setUpdatedMonth] = useState(null);
  const [updatedYear, setUpdatedYear] = useState(null);
  const dispatch = useDispatch();
  const selectedLocation = useSelector(selectedLocationState);
  const {
    isFilterOpen,
    filterUsers,
    searchQuery,
    filteredUserGroups,
    filteredEmployeesBySearch,
    handleFilterToggle,
    handleSearchChange,
  } = useFilteredUserGroups(currentMonthData);

  useEffect(() => {
    dispatch(fetchResourcePermissions('month-view'));
  },[dispatch])

  const updateMonth = (monthName) => {
    setUpdatedMonth(monthName);
  }

  const updateYear = (year) => {
    setUpdatedYear(year);
  }

  const [hasAccess, setHasAccess] = useState(false);

  useEffect(()=>{
    async function checkPageAccess() {
      let access = await checkAccess('month-view', true);
      setHasAccess(access);
      if(!access) window.location.href = "/home";
    }
    checkPageAccess();
  }, []);

  useFetchAirCrafts();

  useEffect(() => {
    const locationId = selectedLocation?.id;
    const token = getToken();

    if (!locationId || !token) {
      return;
    }

    const params = new URLSearchParams({
      locationId: String(locationId),
    });

    let abortController = null;
    let reconnectTimeoutId = null;
    let cancelled = false;

    const handleEvent = (eventName, eventData) => {
      const allowedEvents = ["shift-updated", "shift-created", "shift-deleted"];

      if (!eventData) {
        return;
      }

      let payload = null;
      try {
        payload = JSON.parse(eventData);
      } catch (_error) {
        return;
      }

      const payloadType = (payload?.type || "").toLowerCase();
      const normalizedEventName = (eventName || "").toLowerCase();
      const isShiftChangeEvent =
        allowedEvents.includes(normalizedEventName) ||
        allowedEvents.includes(payloadType);

      if (!isShiftChangeEvent) {
        return;
      }

      const eventLocationId = Number(payload?.locationId);
      const currentLocationId = Number(locationId);
      if (!Number.isNaN(eventLocationId) && eventLocationId !== currentLocationId) {
        return;
      }

      dispatch(updateShiftTransmittedStatusMonth(payload));
    };

    const processSseChunk = (chunk, streamState) => {
      streamState.buffer += chunk;
      const events = streamState.buffer.split("\n\n");
      streamState.buffer = events.pop() ?? "";

      events.forEach((rawEvent) => {
        let eventName = "";
        const dataParts = [];

        rawEvent.split("\n").forEach((line) => {
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          }
          if (line.startsWith("data:")) {
            dataParts.push(line.slice(5).trim());
          }
        });

        handleEvent(eventName, dataParts.join("\n"));
      });
    };

    const connect = async () => {
      abortController = new AbortController();

      try {
        const response = await fetch(
          `${API_BASE_URL}/shifts/stream?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Accept: "text/event-stream",
              Authorization: `Bearer ${token}`,
              "Ocp-Apim-Subscription-Key": HEADER_SUBSCRIPTION_KEY,
            },
            signal: abortController.signal,
          }
        );

        if (!response.ok || !response.body) {
          throw new Error(`Month view SSE response not available: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        const streamState = { buffer: "" };

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) {
            throw new Error("Month view SSE stream ended");
          }

          processSseChunk(decoder.decode(value, { stream: true }), streamState);
        }
      } catch (_error) {
        if (cancelled) {
          return;
        }

        reconnectTimeoutId = setTimeout(() => {
          connect();
        }, 3000);
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
      }
      if (abortController) {
        abortController.abort();
      }
    };
  }, [dispatch, selectedLocation?.id]);

  return (hasAccess &&
    <>
      <MonthViewHeader
        searchQuery={searchQuery}
        isFilterOpen={isFilterOpen}
        handleSearchChange={handleSearchChange}
        handleFilterToggle={handleFilterToggle}
        updatedMonth={updatedMonth}
        updatedYear={updatedYear}
      />
      <div className={classes.monthViewMargin}>
        <MonthView
          filterUsers={filterUsers}
          filteredUserGroups={filteredUserGroups}
          filteredEmployeesBySearch={filteredEmployeesBySearch}
          searchQuery={searchQuery}
          updateMonth={updateMonth}
          updateYear={updateYear}
        />
      </div>
    </>
  );
};

export default MonthViewContainer;